
//each snapshot is going to be limited to what type of change it is

import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Dialog, DialogType } from '@fluentui/react/lib/Dialog';
import { RootState } from 'app/store';
import ActionCard from 'components/controls/ActionCard';
import { Constants } from 'global/Application/Constants';
import { useAuthentication } from 'global/Authentication';
import { ESnapshotStatus } from 'global/Reducer/statusSlice';
import * as IXrmOptions from 'model/AttributeOptionSets.type';
import * as React from 'react';
import { useMemo } from 'react';
import { ConnectedProps, connect } from 'react-redux';

//Per teams 2/8/22 
//everything gets readonly, 
//and in addition: MNIL Data Entry Complete gets create OR reopen; 
//data entry complete gets reopen; 
//in progress gets edit; 
//everything else gets create.

export enum EEntryDialogActions {
    Compare,
    CompareNew,
    Merge,
    MergeNew,
    UnderwritingReviewNew,
    UnderwritingReviewLegacy,
    View,
    Modify,
    ReopenAndModify,
    Create,
}

export enum EEntryDialogVariant {
    Default,
    ViewMultipleConfig,
}

interface IProps extends TComponentConnectedProps {
    variant: EEntryDialogVariant;
    hidden?: boolean;
    isChecklist?: boolean;
    onDismiss?: (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => void;
    onConfirm?: (action: EEntryDialogActions) => void;
}

const EntryDialog = (props: IProps) => {

    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(false);
    const { authorizations } = useAuthentication();

    const styles = {
        root: {
            flexBasis: 'calc(33.333% - 4px)',
            maxWidth: 'calc(33.333% - 4px)',
            minWidth: 180,

        }
    };

    const isUntracked = props.state.status.snapshotStatus === ESnapshotStatus.untracked;
    const snapshot = props.state.data.snapshot;
    const snapshotStatus = snapshot.statuscode;
    const snapshotChangeType = snapshot.bcbs_changetypecode;

    const { dialogModalProps, dialogContentProps } = useMemo(() => {
        const dialogModalProps = {
            isBlocking: true,
            styles: {
                main: { maxWidth: 650 }
            }
        };
        const dialogContentProps = {
            type: DialogType.largeHeader,
            title: 'What would you like to do with these configurations?',
            subText: '',
        };

        return { dialogModalProps, dialogContentProps };
    }, [])

    const isPowerUser = (authorizations?.roles['BCBS - Power User']);

    function onRenderMultipleConfigEntry() {

        return (
            <Dialog
                minWidth={600}
                hidden={props.hidden ?? false}
                onDismiss={props.onDismiss}
                dialogContentProps={dialogContentProps}
                modalProps={dialogModalProps}
            >
                <Stack
                    horizontal
                    wrap
                    horizontalAlign='center'
                    tokens={{ childrenGap: 5 }}
                    styles={{ inner: { flex: '1' } }}
                >

                    {(authorizations?.canView || authorizations?.canEdit) &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Compare'
                                icon='compare'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.CompareNew); }}
                            />
                        </Stack.Item>
                    }

                    {(authorizations?.canView || authorizations?.canEdit) &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Compare (Legacy)'
                                icon='compare'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.Compare); }}
                            />
                        </Stack.Item>
                    }

                    {(props.state.status.debug.admin || Constants.env !== 'Production') &&
                        authorizations?.canEdit &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Merge (Preview)'
                                icon='merge'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.MergeNew); }}
                            />
                        </Stack.Item>
                    }

                    {authorizations?.canEdit &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Merge'
                                icon='merge'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.Merge); }}
                            />
                        </Stack.Item>
                    }

                    {authorizations?.canUnderwritingReviewComplete &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Underwriting Review'
                                icon='view'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.UnderwritingReviewNew); }}
                            />
                        </Stack.Item>
                    }


                    {authorizations?.canUnderwritingReviewComplete &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Underwriting Review (Legacy)'
                                icon='view'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.UnderwritingReviewLegacy); }}
                            />
                        </Stack.Item>
                    }

                </Stack>
            </Dialog>
        );
    }


    function onRenderDefaultEntry() {
        const dialogContentProps = {
            type: DialogType.largeHeader,
            title: 'Select an action for this configuration',
            subText: 'Choose an action to proceed with this configuration.\nAvailable options depend on your permissions and the current status.',
        }

        const isPSAUser = !!authorizations?.roles['BCBS - PSA'];

        const canEditPSA = (
            snapshotChangeType === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['PSA Change(808900012)'].value
        );

        const hasUnderUnderwritingReviewStatus =
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value;

        const hasModifyStatus = (
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value && !isUntracked)
        );

        const hasReopenModifyStatus = (
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value ||
            snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value
        );

        // 302208
        // When status = Retired, if User is NOT Power User or System Admin AND record is NOT Effective Date Gold Standard (bcbs_iseffectivedategoldstandard =/= Yes), do not show Create option.
        const hasCreateStatus = (
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value && isPowerUser && props.state.status.debug.admin) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value && snapshot.bcbs_iseffectivedategoldstandard) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value) ||
            (snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value) ||
            (isUntracked)
        );

        const pendingPowerUserReview = isPowerUser && snapshotStatus === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Pending Power User Review(808900015)'].value;


        let canView = true;
        let canModify = (authorizations?.canEdit || (isPSAUser && canEditPSA)) && hasModifyStatus && !props.isChecklist;
        let canReOpenAndModify = ((authorizations?.canEdit || (isPSAUser && canEditPSA)) && hasReopenModifyStatus) || pendingPowerUserReview;
        let canCreate = (authorizations?.canEdit || isPSAUser) && hasCreateStatus;
        let canUnderwritingReviewComplete = authorizations?.canUnderwritingReviewComplete && hasUnderUnderwritingReviewStatus;

        return (
            <Dialog minWidth={800}
                hidden={hideDialog}
                onDismiss={toggleHideDialog}
                dialogContentProps={dialogContentProps}
                modalProps={dialogModalProps}
            >
                <Stack horizontal wrap horizontalAlign='center' tokens={{ childrenGap: 5 }} styles={{ inner: { flex: '1' } }} >

                    {canView &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='View'
                                icon='view'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.View); }}
                                disabled={isUntracked}
                            />
                        </Stack.Item>
                    }

                    {canModify &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Modify'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.Modify) }}
                            />
                        </Stack.Item>
                    }

                    {canReOpenAndModify &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Reopen and Modify'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.ReopenAndModify) }}
                            />
                        </Stack.Item>
                    }

                    {canCreate &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Create'
                                primary={true}
                                icon='edit'
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.Create) }}
                            />
                        </Stack.Item>
                    }

                    {canUnderwritingReviewComplete &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Underwriting Review'
                                icon='view'
                                primary={true}
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.UnderwritingReviewNew); }}
                            />
                        </Stack.Item>
                    }

                    {canUnderwritingReviewComplete &&
                        <Stack.Item grow={1} styles={styles}>
                            <ActionCard
                                text='Underwriting Review (Legacy)'
                                icon='view'
                                primary={true}
                                onClick={() => { props.onConfirm?.(EEntryDialogActions.UnderwritingReviewLegacy); }}
                            />
                        </Stack.Item>
                    }

                </Stack>
            </Dialog>
        );
    }

    if (props.variant === EEntryDialogVariant.ViewMultipleConfig) {
        return onRenderMultipleConfigEntry();
    }

    return onRenderDefaultEntry();
};

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;

export default connect(mapStateToProps)(EntryDialog);